// Dibujos del bloc de To Do's: marcador, resaltador y figuras guardados como vectores (no imágenes). Funciones puras, con pruebas.
// Un punto es [x, y]: x como fracción del ancho de la hoja (0 a 1) para que el dibujo caiga en el mismo sitio en un iPad y en un Mac;
// y en píxeles desde arriba (las líneas de la hoja miden siempre lo mismo). El grosor es en píxeles.
export type Punto = [number, number];
export type Figura = 'rect' | 'ovalo' | 'flecha' | 'linea' | 'estrella' | 'check' | 'corazon';
export type Trazo =
  | { t: 'marcador' | 'resaltador'; color: string; tam: number; pts: Punto[] }
  | { t: 'figura'; f: Figura; color: string; tam: number; a: Punto; b: Punto };

export const COLORES_MARCADOR = ['#14243B', '#1A5FD0', '#D93A3A', '#1F8A4C', '#8A4FD1', '#FF7A3D'];
export const COLORES_RESALTADOR = ['#FFE066', '#B8F26B', '#FF9EC4', '#8FD8FF', '#FFC48A'];
export const TAM_MARCADOR = [3, 6, 12];
export const TAM_RESALTADOR = [24, 38];
export const FIGURAS: { id: Figura; icono: string; nombre: string }[] = [
  { id: 'rect', icono: '▭', nombre: 'Rectángulo' }, { id: 'ovalo', icono: '◯', nombre: 'Óvalo' }, { id: 'flecha', icono: '➜', nombre: 'Flecha' },
  { id: 'linea', icono: '╱', nombre: 'Línea' }, { id: 'estrella', icono: '★', nombre: 'Estrella' }, { id: 'check', icono: '✓', nombre: 'Check' }, { id: 'corazon', icono: '♥', nombre: 'Corazón' },
];
const FIGS = new Set<string>(FIGURAS.map(f => f.id));
export const MAX_TRAZOS = 400;
export const MAX_PUNTOS = 3000;

const num = (x: unknown): x is number => typeof x === 'number' && Number.isFinite(x);
const punto = (x: unknown): Punto | null => (Array.isArray(x) && num(x[0]) && num(x[1]) && Math.abs(x[0]) < 3 && Math.abs(x[1]) < 100000 ? [x[0], x[1]] : null);
const HEX = /^#[0-9a-f]{6}$/i;

// Lo que viene de la base se revisa antes de dibujarlo: un trazo dañado se descarta, no rompe la hoja.
export function limpiarTrazos(x: unknown): Trazo[] {
  if (!Array.isArray(x)) return [];
  const out: Trazo[] = [];
  for (const r of x.slice(0, MAX_TRAZOS)) {
    if (!r || typeof r !== 'object') continue;
    const o = r as Record<string, unknown>;
    if (typeof o.color !== 'string' || !HEX.test(o.color) || !num(o.tam) || o.tam < 1 || o.tam > 80) continue;
    if (o.t === 'marcador' || o.t === 'resaltador') {
      if (!Array.isArray(o.pts) || !o.pts.length) continue;
      const pts = o.pts.slice(0, MAX_PUNTOS).map(punto);
      if (pts.some(p => !p)) continue;
      out.push({ t: o.t, color: o.color, tam: o.tam, pts: pts as Punto[] });
    } else if (o.t === 'figura' && typeof o.f === 'string' && FIGS.has(o.f)) {
      const a = punto(o.a), b = punto(o.b);
      if (a && b) out.push({ t: 'figura', f: o.f as Figura, color: o.color, tam: o.tam, a, b });
    }
  }
  return out;
}

const r4 = (n: number) => Math.round(n * 10000) / 10000;
const r1 = (n: number) => Math.round(n * 10) / 10;
export const redondear = (p: Punto): Punto => [r4(p[0]), r1(p[1])];
// Quita los puntos que quedan a menos de `minPx` del anterior (el dedo da muchos) y deja siempre el último.
export function simplificar(pts: Punto[], ancho: number, minPx = 1.5): Punto[] {
  if (pts.length < 3) return pts.map(redondear);
  const out: Punto[] = [pts[0]];
  for (let i = 1; i < pts.length - 1; i++) {
    const u = out[out.length - 1];
    if (Math.hypot((pts[i][0] - u[0]) * ancho, pts[i][1] - u[1]) >= minPx) out.push(pts[i]);
  }
  out.push(pts[pts.length - 1]);
  return out.map(redondear);
}

// ¿Está el punto (x fracción, y px) cerca del trazo? Para el borrador, que quita un trazo entero. `ancho` es el ancho de la hoja en px.
export function cercaDe(t: Trazo, p: Punto, ancho: number, radio = 12): boolean {
  const px = p[0] * ancho, py = p[1];
  if (t.t === 'figura') {
    const x1 = Math.min(t.a[0], t.b[0]) * ancho - radio, x2 = Math.max(t.a[0], t.b[0]) * ancho + radio;
    const y1 = Math.min(t.a[1], t.b[1]) - radio, y2 = Math.max(t.a[1], t.b[1]) + radio;
    return px >= x1 && px <= x2 && py >= y1 && py <= y2;
  }
  // Contra los tramos entre puntos (no solo contra los puntos): un trazo rápido deja puntos muy separados.
  return t.pts.some((q, i) => distanciaTramo(px, py, (t.pts[i - 1] ?? q)[0] * ancho, (t.pts[i - 1] ?? q)[1], q[0] * ancho, q[1]) - t.tam / 2 <= radio);
}
function distanciaTramo(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax, dy = by - ay, l2 = dx * dx + dy * dy;
  const u = l2 ? Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / l2)) : 0;
  return Math.hypot(px - (ax + u * dx), py - (ay + u * dy));
}
// El último trazo (el de arriba) que está cerca, o -1.
export function trazoEn(ts: Trazo[], p: Punto, ancho: number, radio = 12): number {
  for (let i = ts.length - 1; i >= 0; i--) if (cercaDe(ts[i], p, ancho, radio)) return i;
  return -1;
}
// Una figura o un trazo demasiado pequeño es un toque, no un dibujo.
export const esToque = (a: Punto, b: Punto, ancho: number) => Math.hypot((b[0] - a[0]) * ancho, b[1] - a[1]) < 6;
